import React from 'react';
import { isProductURLAbsolute } from '@express-labs/raven-tools';
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import LazyImage from '../../../../LazyImage/LazyImage';
import NavLink from '../../../../NavLink/NavLink';
import Link from '../../../../Link/Link';
import IconLeft from '../../../../Carousel/subcomponents/IconLeft';
import IconRight from '../../../../Carousel/subcomponents/IconRight';
import s from './ProductImage.scss';

interface ProductImageBaseProps {
  alt?: string;
  canUseRouter?: boolean;
  className?: string;
  height?: number;
  onError?: (ev: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick?: () => void;
  onLoad?: (ev: React.SyntheticEvent<HTMLImageElement>) => void;
  productId?: string;
  productURL?: string;
  width?: number;
}

export interface ProductImageCarouselProps extends ProductImageBaseProps {
  href?: string;
  imageSet?: string[];
  shouldShowCarouselButtons?: boolean;
  to?: string;
}

interface IsCarousel extends ProductImageBaseProps {
  isScrollable?: true;
  productImage?: never;
  productImageSet: string[];
  shouldShowCarouselButtons?: boolean;
}

interface IsNotCarousel extends ProductImageBaseProps {
  isScrollable?: false;
  productImage: string;
  productImageSet?: never;
  shouldShowCarouselButtons?: never;
}

export type ProductImageProps = ProductImageBaseProps &
  (IsCarousel | IsNotCarousel);

export const ProductImageCarousel = ({
  alt = '',
  height = 243,
  href = '',
  imageSet = [],
  onError,
  onImageClick,
  onLoad,
  productURL,
  shouldShowCarouselButtons = false,
  to = '',
  width = 194,
}: ProductImageCarouselProps) => {
  const totalSlides = imageSet.length;

  if (totalSlides < 1) {
    return null;
  }

  return (
    <CarouselProvider
      visibleSlides={1}
      totalSlides={totalSlides}
      step={1}
      naturalSlideWidth={width}
      naturalSlideHeight={height}
      className={s.productImageCarouselContainer}
    >
      <Slider>
        {imageSet.map((image, index) => (
          <Slide tag="div" index={index} key={image}>
            {productURL && to !== '' && (
              <NavLink to={productURL} onClick={onImageClick}>
                <LazyImage
                  alt={alt}
                  className={s.productImage}
                  height={height}
                  onError={onError}
                  onLoad={onLoad}
                  src={image}
                  width={width}
                />
              </NavLink>
            )}
            {productURL && href !== '' && (
              <Link href={productURL} onClick={onImageClick}>
                <LazyImage
                  alt={alt}
                  className={s.productImage}
                  height={height}
                  onError={onError}
                  onLoad={onLoad}
                  src={image}
                  width={width}
                />
              </Link>
            )}
          </Slide>
        ))}
      </Slider>
      {shouldShowCarouselButtons && (
        <>
          <ButtonBack className={s.back}>
            <IconLeft />
          </ButtonBack>
          <ButtonNext className={s.next}>
            <IconRight />
          </ButtonNext>
        </>
      )}
    </CarouselProvider>
  );
};

const ProductImage = ({
  isScrollable = false,
  alt = '',
  height,
  onError,
  onImageClick,
  onLoad,
  productImage = '',
  productImageSet,
  productURL = '',
  productId = '',
  canUseRouter = true,
  shouldShowCarouselButtons = false,
  width,
}: ProductImageProps) => {
  const isArrayOfImage = productImageSet && Array.isArray(productImageSet);
  const shouldDisplayCarousel = isScrollable && isArrayOfImage;

  const isAbsoluteURL = isProductURLAbsolute(productURL, productId);

  let to = '';
  let href = '';

  if (canUseRouter && !isAbsoluteURL) {
    to = productURL;
  } else {
    href = productURL;
  }

  if (shouldDisplayCarousel) {
    return (
      <ProductImageCarousel
        alt={alt}
        height={height}
        imageSet={productImageSet}
        onError={onError}
        onImageClick={onImageClick}
        onLoad={onLoad}
        to={to}
        href={href}
        productURL={productURL}
        shouldShowCarouselButtons={shouldShowCarouselButtons}
        width={width}
      />
    );
  }

  if (to !== '') {
    return (
      productURL && (
        <NavLink to={productURL} onClick={onImageClick}>
          <LazyImage
            alt={alt}
            className={s.productImage}
            height={height}
            onError={onError}
            onLoad={onLoad}
            src={productImage}
            width={width}
          />
        </NavLink>
      )
    );
  }

  return (
    productURL && (
      <Link href={productURL} onClick={onImageClick}>
        <LazyImage
          alt={alt}
          className={s.productImage}
          height={height}
          onError={onError}
          onLoad={onLoad}
          src={productImage}
          width={width}
        />
      </Link>
    )
  );
};

export default ProductImage;
